import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { disconnectTerminalSession } from "./developAppTerminalApi";

const DevelopAppTerminalContext = createContext(null);

const EMPTY_SESSION = {
  open: false,
  minimized: false,
  device: null,
  credentials: null,
};

export function DevelopAppTerminalProvider({ children }) {
  const [session, setSession] = useState(EMPTY_SESSION);
  const frameRef = useRef(null);

  const nudgeResize = useCallback(() => {
    const win = frameRef.current?.contentWindow;
    if (!win) return;
    try {
      win.dispatchEvent(new Event("resize"));
    } catch {
      // ignore
    }
  }, []);

  const openTerminal = useCallback(({ device, credentials }) => {
    setSession({
      open: true,
      minimized: false,
      device: device || null,
      credentials: credentials || null,
    });
  }, []);

  const minimizeTerminal = useCallback(() => {
    setSession((prev) => (prev.open ? { ...prev, minimized: true } : prev));
  }, []);

  const restoreTerminal = useCallback(() => {
    setSession((prev) => (prev.open ? { ...prev, minimized: false } : prev));
    requestAnimationFrame(() => {
      nudgeResize();
      setTimeout(nudgeResize, 50);
      setTimeout(nudgeResize, 200);
      setTimeout(nudgeResize, 500);
    });
  }, [nudgeResize]);

  const closeTerminal = useCallback(() => {
    setSession((prev) => {
      const deviceId =
        prev.credentials?.deviceId ||
        prev.device?.client_id ||
        prev.device?.device_code ||
        "";
      if (deviceId) {
        disconnectTerminalSession(deviceId).catch(() => {});
      }
      return EMPTY_SESSION;
    });
    frameRef.current = null;
  }, []);

  useEffect(() => {
    const onLogout = () => closeTerminal();
    window.addEventListener("deepedge:logout", onLogout);
    return () => window.removeEventListener("deepedge:logout", onLogout);
  }, [closeTerminal]);

  const value = useMemo(
    () => ({
      ...session,
      isActive: session.open,
      frameRef,
      nudgeResize,
      openTerminal,
      minimizeTerminal,
      restoreTerminal,
      closeTerminal,
    }),
    [
      session,
      nudgeResize,
      openTerminal,
      minimizeTerminal,
      restoreTerminal,
      closeTerminal,
    ]
  );

  return (
    <DevelopAppTerminalContext.Provider value={value}>
      {children}
    </DevelopAppTerminalContext.Provider>
  );
}

export function useDevelopAppTerminal() {
  const ctx = useContext(DevelopAppTerminalContext);
  if (!ctx) {
    throw new Error(
      "useDevelopAppTerminal must be used within DevelopAppTerminalProvider"
    );
  }
  return ctx;
}
